import { useState } from 'react';
import { Avatar, Button, List, Skeleton, Space, Tag, Typography } from 'antd';
import { UserAddOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import {
  useInvitationsQuery,
  useUsersQuery,
  type User,
  type Invitation,
  type UserRole,
} from './hooks';
import { InviteUserModal } from './InviteUserModal';
import { ResetPasswordModal } from './ResetPasswordModal';
import { ErrorAlert } from '@/components/ErrorAlert';
import { useAuthStore } from '@starling/core/auth/store';

const { Text } = Typography;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function RoleTag({ role }: { role: User['role'] }): JSX.Element {
  const { t } = useTranslation();
  if (role === 'admin') return <Tag color="blue">{t('users.role.admin')}</Tag>;
  return <Tag>{t('users.role.employee')}</Tag>;
}

function StatusTag({ status }: { status: User['status'] }): JSX.Element {
  const { t } = useTranslation();
  if (status === 'active') return <Tag color="green">{t('users.status.active')}</Tag>;
  if (status === 'invited') return <Tag color="orange">{t('users.status.invited')}</Tag>;
  return <Tag color="red">{t('users.status.disabled')}</Tag>;
}

export function UsersList(): JSX.Element {
  const { t } = useTranslation();
  const usersQuery = useUsersQuery({ pageSize: 50 });
  const invitationsQuery = useInvitationsQuery({ pageSize: 50 });
  const currentUser = useAuthStore((s) => s.user);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [resendPreset, setResendPreset] = useState<{ email: string; role: UserRole } | null>(null);
  const [resetUser, setResetUser] = useState<User | null>(null);

  const canInvite = currentUser?.role === 'admin';

  const users = usersQuery.data?.items ?? [];
  const invitations = invitationsQuery.data?.items ?? [];

  return (
    <div>
      <Space
        align="center"
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }}
      >
        <Text strong>{t('users.list_title')}</Text>
        {canInvite && (
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setInviteOpen(true)}
            size="small"
          >
            {t('users.invite_button')}
          </Button>
        )}
      </Space>

      {(usersQuery.isLoading || invitationsQuery.isLoading) && <Skeleton active />}
      {usersQuery.error && <ErrorAlert error={usersQuery.error} />}
      {invitationsQuery.error && <ErrorAlert error={invitationsQuery.error} />}

      <List<User>
        dataSource={users}
        renderItem={(u) => (
          <List.Item>
            <List.Item.Meta
              avatar={<Avatar>{initials(u.name) || '?'}</Avatar>}
              title={<Text strong>{u.name}</Text>}
              description={
                <span className="sl-mono" style={{ fontSize: 12 }}>
                  {u.email}
                </span>
              }
            />
            <Space size={6}>
              <RoleTag role={u.role} />
              <StatusTag status={u.status} />
              {canInvite && u.status === 'active' && (
                <Button
                  type="link"
                  size="small"
                  style={{ paddingInline: 0 }}
                  onClick={() => setResetUser(u)}
                >
                  {t('users.reset_password')}
                </Button>
              )}
            </Space>
          </List.Item>
        )}
        locale={{ emptyText: t('users.empty_users') }}
      />

      {invitations.length > 0 && (
        <>
          <Text
            type="secondary"
            style={{
              display: 'block',
              marginTop: 16,
              marginBottom: 8,
              fontSize: 12,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            {t('users.pending_invitations')}
          </Text>
          <List<Invitation>
            dataSource={invitations.filter((i) => !i.accepted_at)}
            renderItem={(inv) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar>?</Avatar>}
                  title={<span className="sl-mono">{inv.email}</span>}
                  description={
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('users.invitation_expires', {
                        date: new Date(inv.expires_at).toLocaleDateString('ru-RU'),
                      })}
                    </Text>
                  }
                />
                <Space size={6}>
                  <RoleTag role={inv.role} />
                  <Tag color="orange">{t('users.status.invited')}</Tag>
                  {canInvite && (
                    <Button
                      type="link"
                      size="small"
                      style={{ paddingInline: 0 }}
                      onClick={() => setResendPreset({ email: inv.email, role: inv.role })}
                    >
                      {t('users.pending_resend')}
                    </Button>
                  )}
                </Space>
              </List.Item>
            )}
            locale={{ emptyText: '' }}
          />
        </>
      )}

      <InviteUserModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <InviteUserModal
        open={resendPreset != null}
        onClose={() => setResendPreset(null)}
        preset={resendPreset ?? undefined}
      />
      <ResetPasswordModal
        open={resetUser != null}
        onClose={() => setResetUser(null)}
        user={resetUser}
      />
    </div>
  );
}
